import React from "react"
import { Icon } from "../shared"

const TUTORIAL_KEY = "osct_tutorial_done"

function getSteps(lang) {
  return [
    {
      targetSelector: '[data-nav="dashboard"]',
      title: lang === "id" ? "Beranda" : "Home",
      description: lang === "id"
        ? "Lihat ringkasan progress belajar, jadwal sesi, dan notifikasi penting Anda."
        : "See your learning progress summary, session schedule, and important notifications.",
    },
    {
      targetSelector: '[data-nav="courses"]',
      title: lang === "id" ? "Kursus" : "Courses",
      description: lang === "id"
        ? "Akses materi pembelajaran, modul, video, dan dokumen kursus Anda."
        : "Access your course materials, modules, videos, and documents.",
    },
    {
      targetSelector: '[data-nav="training"]',
      title: lang === "id" ? "Pelatihan" : "Training",
      description: lang === "id"
        ? "Lihat jadwal sesi pelatihan, konfirmasi kehadiran, dan training hub."
        : "View training session schedules, confirm attendance, and access the training hub.",
    },
    {
      targetSelector: '[data-nav="pretest"]',
      title: lang === "id" ? "Pre-Test" : "Pre-Test",
      description: lang === "id"
        ? "Kerjakan tes awal sebelum pelatihan untuk mengukur pengetahuan awal Anda."
        : "Complete the pre-training test to measure your starting knowledge.",
    },
    {
      targetSelector: '[data-nav="certificates"]',
      title: lang === "id" ? "Sertifikat" : "Certificates",
      description: lang === "id"
        ? "Lihat dan download sertifikat kelulusan Anda setelah menyelesaikan pelatihan."
        : "View and download your completion certificate after finishing training.",
    },
    {
      targetSelector: '[data-nav="ai"]',
      title: "AI Assistant",
      description: lang === "id"
        ? "Tanya AI tentang materi kursus kapan saja. Klik tab di tepi kanan layar untuk membuka."
        : "Ask the AI about course materials anytime. Click the tab on the right edge to open.",
    },
  ]
}

export default function DashboardTutorial({ lang }) {
  const [step, setStep] = React.useState(0)
  const [rect, setRect] = React.useState(null)
  const [visible, setVisible] = React.useState(false)

  React.useEffect(() => {
    if (localStorage.getItem(TUTORIAL_KEY) === "true") return
    // small delay so nav items are mounted
    const t = setTimeout(() => setVisible(true), 600)
    return () => clearTimeout(t)
  }, [])

  const steps = getSteps(lang)

  React.useEffect(() => {
    if (!visible) return
    const s = steps[step]
    const el = document.querySelector(s.targetSelector)
    if (el) {
      const r = el.getBoundingClientRect()
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
      el.scrollIntoView({ block: "nearest", behavior: "smooth" })
    } else {
      setRect(null)
    }
  }, [step, visible])

  function dismiss() {
    localStorage.setItem(TUTORIAL_KEY, "true")
    setVisible(false)
  }

  function next() {
    if (step < steps.length - 1) setStep((s) => s + 1)
    else dismiss()
  }

  function prev() {
    if (step > 0) setStep((s) => s - 1)
  }

  if (!visible) return null

  const PAD = 8
  const highlightStyle = rect
    ? {
        position: "fixed",
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
        borderRadius: 10,
        boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
        zIndex: 2000,
        pointerEvents: "none",
        border: "2px solid var(--program-color, #B84E2A)",
      }
    : null

  // Tooltip: place to the right of target (or below if near right edge)
  const tooltipLeft = rect ? Math.min(rect.left + rect.width + 16, window.innerWidth - 320 - 16) : 40
  const tooltipTop = rect ? Math.max(rect.top - 8, 16) : window.innerHeight / 2 - 80

  const current = steps[step]

  return (
    <>
      {/* Dimmed backdrop (pointer-events only on areas outside highlight) */}
      <div
        style={{ position: "fixed", inset: 0, zIndex: 1999 }}
        onClick={dismiss}
      />

      {/* Highlight cutout */}
      {highlightStyle && <div style={highlightStyle} />}

      {/* Tooltip card */}
      <div
        style={{
          position: "fixed",
          top: tooltipTop,
          left: tooltipLeft,
          width: 300,
          background: "var(--surface)",
          borderRadius: 12,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          zIndex: 2001,
          padding: 20,
          border: "1px solid var(--border)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Step indicator */}
        <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
          {steps.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === step ? 20 : 6,
                height: 6,
                borderRadius: 99,
                background: i === step ? "var(--program-color, #B84E2A)" : "var(--border-strong)",
                transition: "width 0.2s",
              }}
            />
          ))}
        </div>

        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{current.title}</div>
        <div style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.55, marginBottom: 16 }}>
          {current.description}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
          <button
            onClick={dismiss}
            style={{ background: "none", border: "none", fontSize: 12, color: "var(--text-3)", cursor: "pointer", padding: "4px 0" }}
          >
            {lang === "id" ? "Lewati" : "Skip"}
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            {step > 0 && (
              <button
                onClick={prev}
                style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid var(--border-strong)", background: "var(--surface)", fontSize: 13, cursor: "pointer" }}
              >
                ←
              </button>
            )}
            <button
              onClick={next}
              style={{ padding: "6px 16px", borderRadius: 8, border: "none", background: "var(--program-color, #B84E2A)", color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              {step < steps.length - 1 ? (lang === "id" ? "Lanjut" : "Next") : (lang === "id" ? "Selesai" : "Done")} {step < steps.length - 1 ? "→" : "✓"}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
